import {saveOrUpdateChat, eliminarChat} from './chatService';
import getSearchTermAndTitle from './getSearchTerm';
import {addRecipeToFavorites} from '../RecipeDB/recipeService';
import { systemPrompt } from './chatSystemPrompt';
import { v4 as uuidv4 } from 'uuid';

export { systemPrompt }; // <- re-exportar para no romper imports existentes

// Reemplaza la implementación de handleMessageSend por esta:
export const handleMessageSend = async (
  inputValue,
  currentMessages,      // <- NUEVO: recibe el array actual de mensajes directamente
  setMessages,
  userId,
  currentChatId,
  getOpenAIResponse,
  title,
  setChatList,
  setCurrentChatTitle
) => {
  if (!inputValue || !inputValue.trim()) return;

  // Filtrar placeholders de carga para no acumularlos
  const msgs = Array.isArray(currentMessages)
    ? currentMessages.filter(m => m && !m.loading)
    : [];

  const userMessage = {
    id: `msg-${Date.now()}`,
    text: inputValue,
    sender: 'user',
    timestamp: new Date().toISOString()
  };
  const loadingId = `loading-${Date.now()}`;
  const loadingMessage = {
    id: loadingId,
    text: '...',
    sender: 'assistant',
    timestamp: new Date().toISOString(),
    loading: true
  };

  const chatIdToUse = currentChatId || `chat-${Date.now()}`;
  let newTitle = title;

  // Actualizar UI inmediatamente (con placeholder de carga)
  setMessages([...msgs, userMessage, loadingMessage]);

  // Si es el primer mensaje, generar título
  if (msgs.length === 0) {
    const {title: generatedTitle} = getSearchTermAndTitle(inputValue);
    newTitle = generatedTitle;
    setChatList(prev => prev.map(chat =>
      chat.id === chatIdToUse ? {...chat, title: newTitle} : chat
    ));
    setCurrentChatTitle(newTitle);
  }

  // Historial completo a enviar a OpenAI (sin placeholder)
  const messagesToSend = [...msgs, userMessage];

  // Guardar en Firestore inmediatamente (con el mensaje del usuario)
  saveOrUpdateChat(userId, chatIdToUse, messagesToSend, newTitle).catch(console.error);

  try {
    const assistantReply = await getOpenAIResponse(messagesToSend);
    const assistantMessage = {
      id: `msg-${Date.now() + 1}`,
      text: assistantReply,
      sender: 'assistant',
      timestamp: new Date().toISOString()
    };

    // Reemplazar placeholder por respuesta real en UI
    setMessages(prev =>
      (prev || []).map(m => (m && m.id === loadingId) ? assistantMessage : m)
    );

    // Guardar conversación completa en Firestore
    const finalMessages = [...messagesToSend, assistantMessage];
    saveOrUpdateChat(userId, chatIdToUse, finalMessages, newTitle).catch(console.error);

    // Actualizar chatList en memoria (para que al cambiar de chat y volver los mensajes estén)
    setChatList(prev => prev.map(chat =>
      chat.id === chatIdToUse
        ? {...chat, messages: finalMessages, title: newTitle}
        : chat
    ));

  } catch (error) {
    console.error('Error en la conversación:', error);
    setMessages(prev =>
      (prev || []).map(m => (m && m.id === loadingId) ? {
        id: `err-${Date.now()}`,
        text: 'Lo siento, hubo un problema al procesar tu solicitud.',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      } : m)
    );
  }
};

// Función para manejar la creación de un nuevo chat
export const handleNewChat = async (userId, setChatList, setCurrentChatId, saveOrUpdateChat) => {
    const newChatId = `chat-${Date.now()}`; // Crear un ID único para el nuevo chat
    const newTitle = "Nuevo Chat"; // Título inicial
    setCurrentChatId(newChatId);
    
    // Guardar el nuevo chat en Firestore
    const newChat = {id: newChatId, title: newTitle, messages: []};
    setChatList(prevChats => [...prevChats, newChat]); // Agregar a la lista de chats
    await saveOrUpdateChat(userId, newChatId, [], newTitle); // Guardar el nuevo chat vacío

    return newChatId; // <- DEVUELVE el id para que el componente pueda seleccionarlo
};

// Función para seleccionar un chat
export const handleSelectChat = (chatId, chatList, setCurrentChatId, setMessages, setCurrentChatTitle) => {
    const selectedChat = chatList.find(chat => chat.id === chatId);
    setCurrentChatId(chatId);
    setMessages(selectedChat ? selectedChat.messages : []);
    setCurrentChatTitle(selectedChat ? selectedChat.title : "Nuevo Chat");
};

// Función para eliminar un chat
export const handleEliminarChat = async (userId, chatId, setChatList, currentChatId, setCurrentChatId, setMessages, setCurrentChatTitle) => {
    await eliminarChat(userId, chatId);
    setChatList(prevChats => prevChats.filter(chat => chat.id !== chatId));

    // Si el chat eliminado es el actual, resetear el estado de mensajes y el título
    if (currentChatId === chatId) {
        setCurrentChatId(null);
        setMessages([]);
        setCurrentChatTitle("Nuevo Chat");
   }
};

// Función para editar el título de un chat
export const handleEditChatTitle = async (userId, chatId, newTitle, setChatList, setCurrentChatTitle, messages) => {
    try {
        // Actualizar el título en Firestore junto con los mensajes
        await saveOrUpdateChat(userId, chatId, messages, newTitle);

        // Actualizar el título en el listado de chats
        setChatList(prevChats => 
            prevChats.map(chat => 
                chat.id === chatId ? {...chat, title: newTitle} : chat
            )
        );

        // Actualizar el título del chat currente
        setCurrentChatTitle(newTitle);
   } catch (error) {
        console.error('Error al editar el título del chat:', error);
   }
};

// Parsea el texto estructurado del mensaje del asistente y guarda en Recetas Guardadas
export const handleSaveRecipe = async (userId, recipeText) => {
    // El sistema siempre devuelve el formato:
    // **NOMBRE:** ... **INGREDIENTES:** ... **INSTRUCCIONES:** ... **DIFICULTAD:** ... **TIEMPO DE ELABORACIÓN:** ...
    const get = (pattern) => recipeText.match(pattern);

    const nombreMatch  = get(/\*\*NOMBRE:\*\*\s*([^\n*]+)/i);
    const ingMatch     = get(/\*\*INGREDIENTES:\*\*\s*([\s\S]*?)(?=\*\*INSTRUCCIONES:\*\*)/i);
    const instMatch    = get(/\*\*INSTRUCCIONES:\*\*\s*([\s\S]*?)(?=\*\*DIFICULTAD:\*\*)/i);
    const difMatch     = get(/\*\*DIFICULTAD:\*\*\s*([^\n*]+)/i);
    const tiempoMatch  = get(/\*\*TIEMPO[^*]*:\*\*\s*([^\n*]+)/i);

    if (!nombreMatch) throw new Error('No se pudo extraer el nombre de la receta del mensaje.');

    // Ingredientes: soporta lista con guiones, asteriscos o comas en una sola línea
    const ingRaw = ingMatch ? ingMatch[1].trim() : '';
    const ingLines = ingRaw.includes('\n')
        ? ingRaw.split('\n')
        : ingRaw.split(',');
    const ingredientes = ingLines
        .map(s => s.replace(/^[-*\d.]+\s*/, '').trim())
        .filter(Boolean)
        .map(nombre => ({ nombre, cantidad: '', unidad: '' }));

    // Instrucciones: soporta lista numerada o con guiones, y párrafo único
    const instRaw = instMatch ? instMatch[1].trim() : '';
    const instrucciones = instRaw
        .split(/\n|(?<=\.)\s+(?=[A-ZÁ-Ú])/)  // split por salto de línea o frase nueva
        .map(s => s.replace(/^[-*\d.]+\s*/, '').trim())
        .filter(Boolean);

    const recipe = {
        id:             uuidv4(),
        userId,
        nombre:         nombreMatch[1].trim(),
        ingredientes:   ingredientes.length ? ingredientes : [],
        instrucciones:  instrucciones.length ? instrucciones : [],
        dificultad:     difMatch    ? difMatch[1].trim()                        : '',
        tiempoEstimado: tiempoMatch ? parseInt(tiempoMatch[1], 10) || 0         : 0,
        fuente:         'CookyChat',
        imagen:         '',
        imagenes:       [],
        valoraciones:   [],
    };

    await addRecipeToFavorites(userId, recipe);
};